/**
 * Generic CSV-export template persistence. localStorage-backed, capped
 * at 20 entries per moduleId. Stale keys (columns retired since save)
 * are filtered at load.
 *
 * Storage key shape: `spi-ops:{moduleId}:export-templates`. This matches
 * the legacy open-orders shape exactly so existing user templates round-
 * trip without migration.
 */

const TEMPLATE_CAP = 20

export interface ExportTemplate {
  id: string
  label: string
  /** Column keys to include, in export order. Unknown keys are dropped
   *  silently at load time (see loadTemplates). */
  columns: string[]
  createdAt: string
}

function storageKey(moduleId: string): string {
  return `spi-ops:${moduleId}:export-templates`
}

function sanitize(t: ExportTemplate, knownKeys: Set<string>): ExportTemplate {
  return {
    ...t,
    columns: t.columns.filter((k) => knownKeys.has(k)),
  }
}

export function loadTemplates(
  moduleId: string,
  knownKeys: readonly string[],
): ExportTemplate[] {
  try {
    const raw = localStorage.getItem(storageKey(moduleId))
    if (!raw) return []
    const parsed = JSON.parse(raw) as unknown
    if (!Array.isArray(parsed)) return []
    const known = new Set(knownKeys)
    return (parsed as ExportTemplate[])
      .filter(
        (t) =>
          t &&
          typeof t.id === 'string' &&
          typeof t.label === 'string' &&
          Array.isArray(t.columns),
      )
      .map((t) => sanitize(t, known))
      .filter((t) => t.columns.length > 0)
  } catch {
    return []
  }
}

function persistTemplates(moduleId: string, list: ExportTemplate[]): void {
  try {
    localStorage.setItem(storageKey(moduleId), JSON.stringify(list))
  } catch {
    // quota / private mode — non-fatal
  }
}

export function addTemplate(
  moduleId: string,
  knownKeys: readonly string[],
  label: string,
  columns: string[],
): ExportTemplate[] {
  const list = loadTemplates(moduleId, knownKeys)
  const known = new Set(knownKeys)
  const filtered = columns.filter((k) => known.has(k))
  if (filtered.length === 0) return list
  const item: ExportTemplate = {
    id: `t-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    label,
    columns: filtered,
    createdAt: new Date().toISOString(),
  }
  const next = [item, ...list].slice(0, TEMPLATE_CAP)
  persistTemplates(moduleId, next)
  return next
}

export function removeTemplate(
  moduleId: string,
  knownKeys: readonly string[],
  id: string,
): ExportTemplate[] {
  const next = loadTemplates(moduleId, knownKeys).filter((t) => t.id !== id)
  persistTemplates(moduleId, next)
  return next
}
